import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';

import appSettings from '../../../config/settings';
import { useConfiguration } from '../../../context/configurationContext';
import { useRouteConfiguration } from '../../../context/routeConfigurationContext';

import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import { matchPathname, pathByRouteName } from '../../../util/routes';
import { useClientReady } from '../../../util/useClientReady';
import {
  Avatar,
  Button,
  IconArrowHead,
  LimitedAccessBanner,
  ModalMissingInformation,
  NamedLink,
} from '../../../components';

import TopbarDesktop from './TopbarDesktop/TopbarDesktop';
import NavigationIcon from './NavigationIcon';
import ExploreMenu from './ExploreMenu';

import imagotipoImg from '../../../assets/logos/uroborox_v color_6.png';
import css from './Topbar.module.css';
import { getCurrentUserTypeRoles, showCreateListingLinkForUser } from '../../../util/userHelpers';

const MAX_MOBILE_SCREEN_WIDTH = 1024;

const isPrimary = o => o.group === 'primary';
const isSecondary = o => o.group === 'secondary';
const compareGroups = (a, b) => {
  const isAHigherGroupThanB = isPrimary(a) && isSecondary(b);
  const isALesserGroupThanB = isSecondary(a) && isPrimary(b);
  // Note: sort order is stable in JS
  return isAHigherGroupThanB ? -1 : isALesserGroupThanB ? 1 : 0;
};
// Returns links in order where primary links are returned first
const sortCustomLinks = customLinks => {
  const links = Array.isArray(customLinks) ? [...customLinks] : [];
  return links.sort(compareGroups);
};

// Resolves in-app links against route configuration
const getResolvedCustomLinks = (customLinks, routeConfiguration) => {
  const links = Array.isArray(customLinks) ? customLinks : [];
  return links.map(linkConfig => {
    const { type, href } = linkConfig;
    const isInternalLink = type === 'internal' || href.charAt(0) === '/';
    if (isInternalLink) {
      // Internal link
      try {
        const testURL = new URL('http://my.marketplace.com' + href);
        const matchedRoutes = matchPathname(testURL.pathname, routeConfiguration);
        if (matchedRoutes.length > 0) {
          const found = matchedRoutes[0];
          const to = { search: testURL.search, hash: testURL.hash };
          return {
            ...linkConfig,
            route: {
              name: found.route?.name,
              params: found.params,
              to,
            },
          };
        }
      } catch (e) {
        return linkConfig;
      }
    }
    return linkConfig;
  });
};

const isCMSPage = found =>
  found.route?.name === 'CMSPage' ? `CMSPage:${found.params?.pageId}` : null;
const isInboxPage = found =>
  found.route?.name === 'InboxPage' ? `InboxPage:${found.params?.tab}` : null;
// Find the name of the current route/pathname.
// It's used as handle for currentPage check.
const getResolvedCurrentPage = (location, routeConfiguration) => {
  const matchedRoutes = matchPathname(location.pathname, routeConfiguration);
  if (matchedRoutes.length > 0) {
    const found = matchedRoutes[0];
    const cmsPageName = isCMSPage(found);
    const inboxPageName = isInboxPage(found);
    return cmsPageName ? cmsPageName : inboxPageName ? inboxPageName : `${found.route?.name}`;
  }
};

const GenericError = props => {
  const { show } = props;
  const classes = classNames(css.genericError, {
    [css.genericErrorVisible]: show,
  });
  return show ? (
    <div className={classes} role="alert">
      <div className={css.genericErrorContent}>
        <p className={css.genericErrorText}>
          <FormattedMessage id="Topbar.genericError" />
        </p>
      </div>
    </div>
  ) : null;
};

export const TopbarComponent = props => {
  const {
    className,
    rootClassName,
    desktopClassName,
    mobileRootClassName,
    mobileClassName,
    isAuthenticated,
    isLoggedInAs,
    authScopes = [],
    authInProgress,
    currentUser,
    currentUserHasListings,
    currentUserHasOrders,
    currentPage,
    notificationCount = 0,
    intl,
    history,
    location,
    onManageDisableScrolling,
    onResendVerificationEmail,
    sendVerificationEmailInProgress,
    sendVerificationEmailError,
    showGenericError,
    config,
    routeConfiguration,
  } = props;

  const handleLogout = () => {
    const { onLogout, history, routeConfiguration } = props;
    onLogout().then(() => {
      const path = pathByRouteName('LandingPage', routeConfiguration);
      if (appSettings.dev) {
        history.push(path);
      } else if (typeof window !== 'undefined') {
        window.location = path;
      }
      console.log('logged out'); // eslint-disable-line
    });
  };

  const showCreateListingsLink = showCreateListingLinkForUser(config, currentUser);
  const { customer: isCustomer, provider: isProvider } = getCurrentUserTypeRoles(
    config,
    currentUser
  );

  const topbarInboxTab = !isCustomer
    ? 'sales'
    : !isProvider
    ? 'orders'
    : currentUserHasListings
    ? 'sales'
    : 'orders';

  const sortedCustomLinks = sortCustomLinks(config.topbar?.customLinks);
  const customLinks = getResolvedCustomLinks(sortedCustomLinks, routeConfiguration);
  const resolvedCurrentPage = currentPage || getResolvedCurrentPage(location, routeConfiguration);

  const mounted = useClientReady();
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const userMenuRef = useRef(null);
  const userMenuTriggerRef = useRef(null);

  useEffect(() => {
    if (!isUserMenuOpen) return undefined;
    const handleDocClick = e => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setIsUserMenuOpen(false);
      }
    };
    const handleKey = e => {
      if (e.key === 'Escape') {
        setIsUserMenuOpen(false);
        userMenuTriggerRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', handleDocClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleDocClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [isUserMenuOpen]);

  const classes = classNames(rootClassName || css.root, className);
  const authenticatedOnClientSide = mounted && isAuthenticated;

  const showSearchForm = false;

  const handleSkipToMainContent = e => {
    e.preventDefault();
    const mainContent = document.getElementById('main-content');
    if (mainContent) {
      mainContent.scrollIntoView({ behavior: 'smooth', block: 'start' });
      mainContent.setAttribute('tabindex', '-1');
      mainContent.focus();
      mainContent.addEventListener('blur', () => mainContent.removeAttribute('tabindex'), {
        once: true,
      });
    }
  };

  const publishLink = (
    <NamedLink
      name="NewListingPage"
      className={css.mobilePublishLink}
      ariaLabel={intl.formatMessage({ id: 'Topbar.createListing' })}
      title={intl.formatMessage({ id: 'Topbar.createListing' })}
    >
      <FormattedMessage id="Topbar.publishShort" />
    </NamedLink>
  );

  // Keep exploration accessible independently of client-side authentication.
  const browseLink = <ExploreMenu onOpen={() => setIsUserMenuOpen(false)} />;

  // Mobile inline links (no hamburger)
  const mobileAuthLinks = authenticatedOnClientSide ? null : (
    <div className={css.mobileNavLinks}>
      {publishLink}
      <NamedLink
        name="LoginPage"
        className={css.mobileLoginLink}
        ariaLabel={intl.formatMessage({ id: 'Topbar.login' })}
        title={intl.formatMessage({ id: 'Topbar.login' })}
      >
        <NavigationIcon variant="account" />
      </NamedLink>
    </div>
  );

  const closeUserMenu = () => setIsUserMenuOpen(false);

  useEffect(() => {
    setIsUserMenuOpen(false);
  }, [location.key, location.pathname]);

  const mobileUserLinks = authenticatedOnClientSide ? (
    <div className={css.mobileNavLinks}>
      {showCreateListingsLink ? publishLink : null}
      <NamedLink
        name="InboxPage"
        params={{ tab: topbarInboxTab }}
        className={classNames(css.mobileLoginLink, css.tabletInboxLink)}
        ariaLabel={intl.formatMessage({ id: 'TopbarDesktop.inbox' })}
        title={intl.formatMessage({ id: 'TopbarDesktop.inbox' })}
      >
        <NavigationIcon variant="inbox" className={css.mobileActionIcon} />
        <span className={css.mobileActionText}>
          <FormattedMessage id="TopbarDesktop.inbox" />
        </span>
        {notificationCount > 0 ? (
          <span className={css.mobileNotificationDot} aria-hidden="true" />
        ) : null}
      </NamedLink>
      <div className={css.mobileUserMenu} ref={userMenuRef}>
        <button
          type="button"
          ref={userMenuTriggerRef}
          className={css.mobileAvatarTrigger}
          onClick={() => setIsUserMenuOpen(prev => !prev)}
          aria-label={intl.formatMessage({ id: 'Topbar.openUserMenu' })}
          aria-expanded={isUserMenuOpen}
          aria-controls="mobile-user-navigation"
        >
          <Avatar user={currentUser} disableProfileLink className={css.mobileAvatar} />
          {notificationCount > 0 ? (
            <span className={css.mobileNotificationDot} aria-hidden="true" />
          ) : null}
        </button>
        {isUserMenuOpen ? (
          <div id="mobile-user-navigation" className={css.mobileUserDropdown}>
            <NamedLink
              name="InboxPage"
              params={{ tab: topbarInboxTab }}
              className={classNames(css.mobileUserItem, css.phoneInboxItem)}
            >
              <FormattedMessage id="TopbarDesktop.inbox" />
              {notificationCount > 0 ? <span className={css.menuUnreadCount}>{notificationCount}</span> : null}
            </NamedLink>
            {showCreateListingsLink ? (
              <NamedLink
                name="ManageListingsPage"
                className={css.mobileUserItem}
                onClick={closeUserMenu}
              >
                <FormattedMessage id="TopbarDesktop.yourListingsLink" />
              </NamedLink>
            ) : null}
            <NamedLink
              name="AccountSettingsPage"
              className={css.mobileUserItem}
              onClick={closeUserMenu}
            >
              <FormattedMessage id="TopbarDesktop.accountSettingsLink" />
            </NamedLink>
            <a
              href="mailto:soporte@uroborox.io?subject=Soporte%20Uroborox"
              className={css.mobileUserItem}
              onClick={closeUserMenu}
            >
              <FormattedMessage id="Topbar.support" />
            </a>
            <button
              type="button"
              className={css.mobileUserItem}
              onClick={() => {
                closeUserMenu();
                handleLogout();
              }}
            >
              <FormattedMessage id="TopbarDesktop.logout" />
            </button>
          </div>
        ) : null}
      </div>
    </div>
  ) : null;

  return (
    <div className={classes}>
      <Button onClick={handleSkipToMainContent} className={css.skipToMainContent}>
        <FormattedMessage id="Topbar.skipToMainContent" />
        <IconArrowHead direction="right" size="small" rootClassName={css.skiptoMainArrow} />
      </Button>
      <LimitedAccessBanner
        isAuthenticated={isAuthenticated}
        isLoggedInAs={isLoggedInAs}
        authScopes={authScopes}
        currentUser={currentUser}
        onLogout={handleLogout}
        currentPage={resolvedCurrentPage}
      />
      <nav
        className={classNames(mobileRootClassName || css.container, mobileClassName)}
        aria-label={intl.formatMessage({ id: 'TopbarDesktop.screenreader.topbarNavigation' })}
      >
        <NamedLink name="LandingPage" className={css.mobileLogoLink}>
          <img src={imagotipoImg} alt="Uroborox" className={css.mobileLogoImg} />
        </NamedLink>
        <div className={css.mobileActions}>
          {browseLink}
          {mobileAuthLinks}
          {mobileUserLinks}
        </div>
      </nav>
      <div className={css.desktop}>
        <TopbarDesktop
          className={desktopClassName}
          currentUserHasListings={currentUserHasListings}
          currentUser={currentUser}
          currentPage={resolvedCurrentPage}
          intl={intl}
          isAuthenticated={isAuthenticated}
          notificationCount={notificationCount}
          onLogout={handleLogout}
          config={config}
          customLinks={customLinks}
          showSearchForm={showSearchForm}
          showCreateListingsLink={showCreateListingsLink}
          inboxTab={topbarInboxTab}
        />
      </div>
      <ModalMissingInformation
        id="MissingInformationReminder"
        containerClassName={css.missingInformationModal}
        currentUser={currentUser}
        currentUserHasListings={currentUserHasListings}
        currentUserHasOrders={currentUserHasOrders}
        location={location}
        onManageDisableScrolling={onManageDisableScrolling}
        onResendVerificationEmail={onResendVerificationEmail}
        sendVerificationEmailInProgress={sendVerificationEmailInProgress}
        sendVerificationEmailError={sendVerificationEmailError}
      />

      <GenericError show={showGenericError} />
    </div>
  );
};

/**
 * Topbar containing logo, main search and navigation links.
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {Object} props.desktopClassName add more style rules for TopbarDesktop
 * @param {Object} props.mobileRootClassName overwrite mobile layout root classes
 * @param {Object} props.mobileClassName add more style rules for mobile layout
 * @param {boolean} props.isAuthenticated
 * @param {boolean} props.isLoggedInAs
 * @param {Object} props.currentUser
 * @param {boolean} props.currentUserHasListings
 * @param {boolean} props.currentUserHasOrders
 * @param {string} props.currentPage
 * @param {number} props.notificationCount
 * @param {Function} props.onLogout
 * @param {Function} props.onManageDisableScrolling
 * @param {Function} props.onResendVerificationEmail
 * @param {Object} props.sendVerificationEmailInProgress
 * @param {Object} props.sendVerificationEmailError
 * @param {boolean} props.showGenericError
 * @param {Object} props.history
 * @param {Function} props.history.push
 * @param {Object} props.location
 * @param {string} props.location.search '?foo=bar'
 * @returns {JSX.Element} topbar component
 */
const Topbar = props => {
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const intl = useIntl();
  return (
    <TopbarComponent
      config={config}
      routeConfiguration={routeConfiguration}
      intl={intl}
      {...props}
    />
  );
};

export default Topbar;
